import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './BookCard.css';

function BookCard({
  book,
  isFavorite = false,
  userRating = 0,
  onFavorite,
  onRate,
  showDeleteButton = false,
  onDelete,
  showDetailsLink = true,
  linkTo,
  linkState,
  showRatingButton = true,
  showFavoriteButton = true,
  showRating = false,
  compact = false
}) {
  const imageRef = useRef(null);
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hoveredStarIndex, setHoveredStarIndex] = useState(-1);
  const [optimisticFavorite, setOptimisticFavorite] = useState(isFavorite);
  const [optimisticRating, setOptimisticRating] = useState(userRating);
  const [loadingFavorite, setLoadingFavorite] = useState(false);
  const [loadingRating, setLoadingRating] = useState(false);

  useEffect(() => {
    if (imageLoaded && imageRef.current) {
      const img = imageRef.current;
      if (img.naturalWidth > 0) {
        const container = img.parentElement;
        const maxHeight = container.offsetHeight;
        const maxWidth = container.offsetWidth * 0.7;

        const scale = Math.min(
          maxHeight / img.naturalHeight,
          maxWidth / img.naturalWidth,
          1
        );

        const targetWidth = Math.floor(img.naturalWidth * scale);
        const targetHeight = Math.floor(img.naturalHeight * scale);

        img.style.width = `${targetWidth}px`;
        img.style.height = `${targetHeight}px`;
      }
    }
  }, [imageLoaded]);

  const handleStarClick = (index, e) => {
    e.stopPropagation();
    const newRating = (index + 1) * 2;
    setOptimisticRating(newRating);
    setLoadingRating(true);

    if (onRate) {
      onRate(newRating)
        .then(() => {
          setLoadingRating(false);
        })
        .catch((error) => {
          console.error('Error rating book:', error);
          setOptimisticRating(userRating);
          setLoadingRating(false);
        });
    }
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    setOptimisticFavorite(!optimisticFavorite);
    setLoadingFavorite(true);

    if (onFavorite) {
      onFavorite()
        .then(() => {
          setLoadingFavorite(false);
        })
        .catch((error) => {
          console.error('Error favoriting book:', error);
          setOptimisticFavorite(isFavorite);
          setLoadingFavorite(false);
        });
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete();
  };

  const handleStarHover = (index) => {
    setHoveredStarIndex(index);
  };

  const handleStarHoverEnd = () => {
    setHoveredStarIndex(-1);
  };

  const handleCardClick = () => {
    if (!showDetailsLink) return;

    const detailsLink = linkTo || `/favorite/${book.id}`;
    const detailsState = linkState || { bookId: book.id, book };
    navigate(detailsLink, { state: detailsState });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCardClick();
    }
  };

  const shouldBeCompact = compact ||
    (!showRatingButton && !showFavoriteButton) ||
    (showDetailsLink && !showRatingButton && !showFavoriteButton);

  const isSearchResult = !showDeleteButton;

  return (
    <div
      className={`book-card ${shouldBeCompact ? 'compact' : ''} ${isSearchResult ? 'search-result' : ''}`}
      onClick={handleCardClick}
      role={showDetailsLink ? 'button' : undefined}
      tabIndex={showDetailsLink ? 0 : undefined}
      onKeyDown={showDetailsLink ? handleKeyPress : undefined}
    >
      {showDeleteButton && (
        <button
          className="delete-icon-button"
          onClick={handleDeleteClick}
          aria-label="Remove from favorites"
        >
          🗑
        </button>
      )}
      <div className="book-card-ribbon"></div>
      <div className="book-card-content">
        <div className="book-image-container">
          {book.thumbnail && (
            <img
              ref={imageRef}
              src={book.thumbnail}
              alt={book.title}
              className="book-image"
              loading="eager"
              decoding="sync"
              onLoad={() => setImageLoaded(true)}
              draggable="false"
            />
          )}
          {showRating && userRating > 0 && (
            <div className="rating-badge">{userRating}/10</div>
          )}
        </div>
        <h2 className="book-title">{book.title}</h2>
        {book.authors?.length > 0 && (
          <p className="book-authors">{book.authors.join(', ')}</p>
        )}
      </div>

      <div className="card-controls" onClick={(e) => e.stopPropagation()}>
        {showRatingButton && (
          <div className="book-rating" onMouseLeave={handleStarHoverEnd}>
            {[...Array(5)].map((_, index) => (
              <button
                key={index}
                className={`star ${index < optimisticRating / 2 ? 'filled' : ''} ${index <= hoveredStarIndex ? 'hovered' : ''}`}
                onClick={(e) => handleStarClick(index, e)}
                onMouseOver={() => handleStarHover(index)}
                disabled={loadingRating}
              >
                ★
              </button>
            ))}
          </div>
        )}
        {showFavoriteButton && (
          <button
            className={`favorite-button ${optimisticFavorite ? 'favorited' : ''}`}
            onClick={handleFavoriteClick}
            disabled={loadingFavorite}
            aria-label={optimisticFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            {loadingFavorite ? 'Saving...' : optimisticFavorite ? '❤️ Saved' : '🤍 Save'}
          </button>
        )}
      </div>
    </div>
  );
}

export default BookCard;
